import { create } from 'zustand';
import { apiUrl } from '../api/api-url';
import { appClient, type HttpClient } from '../api/http';
import type { PreferencesSummary, SavePreferencesRequest } from '../api/types';
import { DEGRADED_FALLBACK } from './defaults';
import { applyTheme } from '../theme/theme-controller';
import { asDateFormatFamily, asTitleDateVariant } from '../../shared/actions/model/dates';
import { SESSION_TIMEOUT_MS } from '../session/session-timeout';

interface PreferencesState {
  /** `null` until a read settles. Not the same as "the defaults are in force": before a read there
   *  is nothing to render, and the session store's `adopt()` is what guarantees no screen that
   *  depends on a preference is reachable in this state (FR-011). */
  readonly values: PreferencesSummary | null;
  /** True only while the defaults are standing in for values that could not be read (FR-012). */
  readonly degraded: boolean;
}

/**
 * The nine per-practitioner settings, held for the session.
 *
 * **Module-level, and that is not stylistic.** The session store's `adopt()` fills it, from outside
 * any component tree — the same choke point `restore()`, `signIn()` and `renewOnce()` all pass
 * through, which is what makes FR-011's "available before the first render of any screen that
 * depends on them" hold by construction rather than by every screen remembering to wait. A React
 * context would have tied it to a provider and put it back inside the tree.
 *
 * **The load takes its client from the caller; the save does not.** `adopt()` passes `directClient`
 * — bypassing the renewing wrapper — and the access token it has just been handed. Going through
 * the renewing client instead would let this read 401 and re-enter the renewal it is running
 * inside: through `renew()` it awaits its own in-flight promise and hangs, through `restore()` it
 * recurses. `session.store.ts`'s own header says the same about session traffic. A **save** is the
 * opposite case — an ordinary user action on a live session — so it uses `appClient()` and renews
 * like every other write.
 *
 * **`load` never rejects** (R-011). A preferences read is not allowed to take sign-in down with it,
 * and every way it can fail — unauthorised, unavailable, malformed — produces the same degraded
 * state, because the practitioner has the same recourse in each case. `save` does the opposite: a
 * refusal must reach the settings screen, which names the field and its range.
 */
const store = create<PreferencesState>()(() => ({ values: null, degraded: false }));

/**
 * What is actually governing the screens. `values === null` is only reachable before the first read
 * settles, which is before any screen that depends on a preference exists — see `adopt()`. The
 * fallback here is FR-015's single permitted client-side statement of the defaults, and it is
 * deliberately not an initial value: nothing may paint a default and then correct itself.
 */
const effective = (s: PreferencesState): PreferencesSummary => s.values ?? DEGRADED_FALLBACK;

/**
 * The one reading of the browser's own zone. Both the effective zone below and the settings
 * screen's "use my browser's zone" offer take it from here rather than calling `Intl` a second
 * time.
 */
export const browserZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Every selector is exported twice: a `use…` form for components, and a plain form for the code
 * that reads preferences from outside React — quick-add, the export builders, the date helpers.
 * The Angular version got both from one `computed`; here the two callers genuinely differ, and
 * pretending otherwise would mean a hook called from a non-component.
 *
 * Both read through `effective`, so neither can drift from the other about what a missing value
 * means.
 */
const select = <T>(pick: (p: PreferencesSummary) => T) => ({
  use: (): T => store((s) => pick(effective(s))),
  get: (): T => pick(effective(store.getState())),
});

export const completionsShown = select((p) => p.completionsShown);
export const reviewPeriodDays = select((p) => p.reviewPeriodDays);
export const stalenessStartsDays = select((p) => p.stalenessStartsDays);
export const showCompletedProjectsInSidebar = select((p) => p.showCompletedProjectsInSidebar);
export const showHiddenContextsInSidebar = select((p) => p.showHiddenContextsInSidebar);
/** The three display preferences (015): always a resolved spelling — absence is not a value on this
 *  wire, so unlike `timeZone` there is no null to carry. */
export const dateFormat = select((p) => asDateFormatFamily(p.dateFormat));
export const titleDateFormat = select((p) => asTitleDateVariant(p.titleDateFormat));
export const theme = select((p) => p.theme);
export const weekStart = select((p) => p.weekStart);
/** 018: the notifications cadence, resolved on the wire like the display preferences. */
export const emailNotifications = select((p) => p.emailNotifications);
/**
 * 023: the practitioner's STORED choice of capture context, or `null` if they have not made one.
 * Not resolved to their Inbox — resolution happens on the server at capture, and the settings
 * screen renders `null` as "My Inbox".
 */
export const captureContextId = select((p) => p.captureContextId);
/** `null` means "never chosen" (010 FR-002) — distinct from the other five, which are never
 *  themselves absent once a read has settled. */
export const timeZone = select((p) => p.timeZone);
/**
 * FR-001's first two rungs, client-side: the practitioner's stored zone if they chose one, else the
 * zone the browser itself reports. The third rung — UTC — belongs to the server's resolver and is
 * never restated here (research R-003); a practitioner with no stored zone and no readable browser
 * zone is not a case this client can distinguish from one who simply has not chosen, so there is
 * nothing to fall back to beyond the browser.
 */
export const effectiveZone = select((p) => p.timeZone ?? browserZone());

export const isDegraded = { use: (): boolean => store((s) => s.degraded), get: (): boolean => store.getState().degraded };

/**
 * 036 R-003 — the whole record `session.store.ts`'s `adopt()` persists to `completeme.identity`,
 * "as last confirmed". Unlike every selector above, this hands back the whole `PreferencesSummary`
 * rather than one field, because the identity is a snapshot for a session that could not be
 * confirmed, not a live binding a screen renders from — `effectiveZone` and the rest stay the ones
 * a component reads from.
 */
export const currentPreferences = { get: (): PreferencesSummary => effective(store.getState()) };

/**
 * 036 R-003 — `session.store.ts`'s `restore()` fast path calls this to put what `completeme.identity`
 * held back into effect BEFORE anything has been asked of the server: the `unconfirmed` screen reads
 * a time zone, a date style and a theme from here exactly as a signed-in one does. Not a "read" —
 * `degraded` is left `false`, since these values came from a real prior confirmation and are not the
 * FR-012 fallback standing in for one that failed. The renewal `restore()` starts alongside this
 * still calls `loadPreferences` itself once it settles (FR-011), which replaces this the moment the
 * server actually answers.
 */
export function adoptStoredPreferences(values: PreferencesSummary): void {
  store.setState({ values, degraded: false });
}

const url = (): string => apiUrl('/api/v1/me/preferences');

/** At most one read at a time, so two `adopt()`s in flight cannot race each other's outcome. */
let reading: Promise<void> | null = null;

async function read(accessToken: string, client: HttpClient): Promise<void> {
  try {
    const values = await client.get<PreferencesSummary>(url(), {
      headers: { Authorization: `Bearer ${accessToken}` },
      // 036 R-006: this read rides inside a renewal (`adopt()`) as often as it runs on its own, so
      // it carries the same five-second session-traffic limit as the renewal itself.
      timeoutMs: SESSION_TIMEOUT_MS,
    });
    store.setState({ values, degraded: false });
  } catch {
    // Degrade only when there is nothing held. `adopt()` re-reads on every silent renewal (FR-011),
    // which is also how a degraded session recovers (FR-012); the other direction must not happen.
    // Replacing values the practitioner actually saved with the defaults because one renewal's read
    // failed is exactly what FR-011 forbids.
    if (store.getState().values === null) {
      store.setState({ values: DEGRADED_FALLBACK, degraded: true });
    }
  }
}

/**
 * Read this practitioner's preferences with the token `adopt()` was handed, on the client it
 * supplies. Resolves either way; `isDegraded` says which happened.
 */
export function loadPreferences(accessToken: string, client: HttpClient): Promise<void> {
  reading ??= read(accessToken, client).finally(() => {
    reading = null;
  });
  return reading;
}

/**
 * Save the whole record. The held values are replaced by what the *response* carries, not by what
 * was sent: the server is what decides, and echoing the request back would hide a server that
 * stored something else.
 *
 * Refused while degraded (FR-012) — a whole-record save composed from five defaults the application
 * never read would silently overwrite five stored values. The settings screen disables the control;
 * this refuses regardless, so the guarantee does not rest on a template.
 */
export async function savePreferences(body: SavePreferencesRequest): Promise<void> {
  if (store.getState().degraded) {
    throw new Error('Preferences could not be read, so they cannot be saved.');
  }

  const values = await appClient().put<PreferencesSummary>(url(), body);
  store.setState({ values });
  // 015 FR-012 (theme half): a saved change re-themes the same tab at once — and from the RESPONSE,
  // like the values above, because the server is what decides.
  applyTheme(values.theme);
  for (const listener of savedListeners) listener(values);
}

/**
 * 036 (issue 219) — `session.store.ts`'s `completeme.identity` is a snapshot `adopt()` takes at
 * sign-in or renewal time, "as last confirmed" (R-003). A preference the practitioner changes
 * mid-session — the theme, most visibly — updates the LIVE store above at once (FR-013) but never
 * touched that snapshot, which went stale until the next renewal rewrote it. Harmless before this
 * cycle, since nothing read the snapshot back; `restore()`'s fast path now does, to draw a screen
 * before the server has answered, and a stale theme in it painted one light frame of an
 * authenticated screen where SC-004 promises zero (found by `e2e/theme.spec.ts`'s own "after a
 * reload the first authenticated paint is already dark" going red against this file unchanged).
 *
 * A plain subscription rather than a direct call into `session.store.ts`, because that file already
 * imports FROM this one (`adoptStoredPreferences`, `loadPreferences`, `theme`) — a call the other
 * way would be the exact circular import both files' own headers already refuse to reintroduce.
 */
type PreferencesSavedListener = (values: PreferencesSummary) => void;
const savedListeners = new Set<PreferencesSavedListener>();

/** `core/session/session.store.ts`'s own use. Returns the unsubscribe; production code never calls
 *  it more than once, at module load. */
export function onPreferencesSaved(listener: PreferencesSavedListener): () => void {
  savedListeners.add(listener);
  return () => savedListeners.delete(listener);
}

/**
 * Test seam. `values === null` is not observable through any selector — every one of them reads
 * through `effective`, which is the point — so a spec that wants to distinguish "nothing read yet"
 * from "the defaults are in force" needs this. Production code never calls it.
 */
export const peekPreferences = (): { values: PreferencesSummary | null; degraded: boolean } => store.getState();

/** Test seam: resets held values between cases. Production code never calls it. */
export function resetPreferences(): void {
  store.setState({ values: null, degraded: false });
  reading = null;
}

/**
 * 036 (issue 258, B3) — `core/session/session.store.ts`'s `clear()` calls this on every sign-out and
 * every refusal of the session. Without it, `values` simply stayed whatever the OUTGOING
 * practitioner's own `adopt()` last read — `read()`'s catch branch degrades to the fallback only
 * `if (store.getState().values === null)`, which was never true once SOMEONE had successfully signed
 * in — so a practitioner who then signed in on the same browser, with their own `/me/preferences`
 * read failing, had the PREVIOUS practitioner's time zone, date style and theme silently persisted as
 * their own `completeme.identity` snapshot. Sets the DEGRADED fallback, never `null` (unlike
 * `resetPreferences` above): `null` means "nothing has ever been read in this tab," which `adopt()`'s
 * own FR-011 guarantee says is unreachable once a screen exists, and leaving it would have a
 * mid-session consumer of `effective()` fall through to the very same fallback value anyway — stating
 * it explicitly, with `degraded: true`, is what makes `isDegraded` tell the truth about a session that
 * has not yet read anyone's preferences.
 */
export function resetPreferencesToFallback(): void {
  store.setState({ values: DEGRADED_FALLBACK, degraded: true });
  reading = null;
}
